import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, MapPin, Clock, Calendar, ChevronDown, ChevronUp, Heart, Send } from 'lucide-react';
import RSVPForm from './RSVPForm';

// ─── Scroll-reveal wrapper ─────────────────────────────────────────
function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ─── Countdown logic ───────────────────────────────────────────────
function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    if (!targetDate) return;
    const tick = () => {
      const diff = Math.max(0, new Date(targetDate).getTime() - Date.now());
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return timeLeft;
}

// ─── Floating Audio Toggle ─────────────────────────────────────────
function AudioToggle({ src }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); }
    else { a.volume = 0.6; a.play().catch(() => {}); }
    setPlaying(p => !p);
  }, [playing]);

  if (!src) return null;
  return (
    <>
      <audio ref={audioRef} src={src} loop preload="auto" />
      <button
        onClick={toggle}
        className="fixed top-5 right-5 z-50 w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-md transition-all duration-300 shadow-lg"
        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}
        data-testid="audio-toggle"
      >
        {playing
          ? <Volume2 className="w-4 h-4 text-white drop-shadow" />
          : <VolumeX className="w-4 h-4 text-white/60 drop-shadow" />
        }
      </button>
    </>
  );
}

// ─── Sticky RSVP Button ────────────────────────────────────────────
function StickyRSVP({ accent, onClick, visible }) {
  return (
    <motion.button
      initial={{ y: 80 }}
      animate={{ y: visible ? 0 : 80 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      onClick={onClick}
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-8 py-3.5 rounded-full font-medium text-sm tracking-wider uppercase shadow-2xl transition-colors"
      style={{ background: accent, color: '#fff', boxShadow: `0 8px 32px ${accent}55` }}
      data-testid="sticky-rsvp-btn"
    >
      <Heart className="w-4 h-4 inline mr-2 -mt-0.5" />
      RSVP
    </motion.button>
  );
}

// ─── FAQ Accordion ─────────────────────────────────────────────────
function FAQItem({ q, a, accent }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span className="text-sm font-medium pr-4">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: accent }} /> : <ChevronDown className="w-4 h-4 flex-shrink-0 opacity-40" />}
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <p className="pb-4 text-sm opacity-60 leading-relaxed">{a}</p>
      </motion.div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN TEMPLATE
// ═══════════════════════════════════════════════════════════════════
export default function PremiumStory({ data, onSubmitRSVP, isSubmitting, submitSuccess }) {
  const {
    couple_names = '',
    event_date = '',
    venue_name = '',
    story_text = '',
    hero_photos = [],
    hero_image_url = '',
    hero_bg_image = '',
    hero_layout = 'full_width',
    gallery_images = [],
    timeline = [],
    background_music_url = '',
    song_title = '',
    artist_name = '',
    color_theme = {},
    font_style = 'serif',
    faq_items = [],
    invitation_message = '',
    venue_address = '',
  } = data || {};

  const accent = color_theme?.accent || '#c08081';
  const primary = color_theme?.primary || '#2c2420';
  const secondary = color_theme?.secondary || '#8a8278';
  const bg = color_theme?.background || '#faf8f5';
  const isDark = isColorDark(bg);
  const textColor = isDark ? '#f5f2ef' : primary;
  const textMuted = isDark ? 'rgba(255,255,255,0.55)' : secondary;
  const cardBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)';
  const borderC = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

  const heroImg = hero_photos?.[0] || hero_image_url || hero_bg_image || '';
  const rsvpRef = useRef(null);
  const [showSticky, setShowSticky] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);

  // Show sticky RSVP after scrolling past hero
  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > window.innerHeight * 0.7);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToRSVP = () => rsvpRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const countdown = useCountdown(event_date);

  const fontClass = { serif: 'font-serif', sans: 'font-sans', script: 'font-serif italic' }[font_style] || 'font-serif';

  // Format date nicely
  const formattedDate = event_date ? new Date(event_date + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  }) : '';

  const defaultFAQ = [
    { q: 'What is the dress code?', a: 'Semi-formal or cocktail attire. Please be comfortable and elegant.' },
    { q: 'Can I bring a plus one?', a: 'Please refer to your invitation for guest details. If you have questions, contact us.' },
    { q: 'Is there parking available?', a: 'Yes, complimentary parking is available at the venue.' },
    { q: 'Will there be a vegetarian option?', a: 'Yes! Please mention any dietary requirements in the RSVP form.' },
  ];
  const faqs = (faq_items && faq_items.length > 0) ? faq_items : defaultFAQ;

  return (
    <div className={fontClass} style={{ background: bg, color: textColor, minHeight: '100vh' }}>
      {/* Persistent UI */}
      <AudioToggle src={background_music_url} />
      <StickyRSVP accent={accent} onClick={scrollToRSVP} visible={showSticky} />

      {/* ─── Section 1: Hero Cover ─────────────────────────────── */}
      <section
        className="relative flex flex-col items-center justify-center text-center overflow-hidden"
        style={{ minHeight: '100svh' }}
        data-testid="hero-section"
      >
        {/* Background image */}
        {heroImg && (
          <div className="absolute inset-0">
            <img
              src={heroImg}
              alt=""
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div
              className="absolute inset-0"
              style={{
                background: isDark
                  ? 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)'
                  : 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.45) 100%)',
              }}
            />
          </div>
        )}

        {/* Hero content */}
        <div className="relative z-10 px-6 max-w-sm">
          <Reveal>
            <p
              className="text-xs tracking-[0.35em] uppercase mb-6 font-sans"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              We're getting married
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <h1
              className="text-4xl sm:text-5xl leading-tight mb-4 font-light"
              style={{ color: '#ffffff', textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}
            >
              {couple_names || 'Our Wedding'}
            </h1>
          </Reveal>
          <Reveal delay={0.3}>
            <div
              className="w-12 h-px mx-auto mb-4"
              style={{ background: accent }}
            />
            <p
              className="text-sm tracking-wider font-sans"
              style={{ color: 'rgba(255,255,255,0.8)' }}
            >
              {formattedDate}
            </p>
            {venue_name && (
              <p className="text-xs mt-2 tracking-wider font-sans" style={{ color: 'rgba(255,255,255,0.6)' }}>
                {venue_name}
              </p>
            )}
          </Reveal>
        </div>

        {/* Scroll hint */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.4)' }} />
        </motion.div>
      </section>

      {/* ─── Section 2: Invitation Message ─────────────────────── */}
      <section className="px-5 py-20 sm:py-24" data-testid="invitation-section">
        <div className="max-w-sm mx-auto text-center">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase mb-6 font-sans" style={{ color: accent }}>
              With Love
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-base sm:text-lg leading-relaxed" style={{ color: textColor, opacity: 0.85 }}>
              {invitation_message || story_text || `Together with our families, we joyfully invite you to celebrate the beginning of our new life together. Your presence would mean the world to us.`}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex justify-center">
              <div className="w-16 h-px" style={{ background: borderC }} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── Section 3: Countdown ──────────────────────────────── */}
      {event_date && (
        <section className="px-5 pb-20" data-testid="countdown-section">
          <div className="max-w-sm mx-auto text-center">
            <Reveal>
              <p className="text-xs tracking-[0.3em] uppercase mb-8 font-sans" style={{ color: accent }}>
                Counting Down
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { val: countdown.days, label: 'Days' },
                  { val: countdown.hours, label: 'Hours' },
                  { val: countdown.minutes, label: 'Minutes' },
                  { val: countdown.seconds, label: 'Seconds' },
                ].map(({ val, label }) => (
                  <div
                    key={label}
                    className="rounded-2xl py-4 px-2"
                    style={{ background: cardBg, border: `1px solid ${borderC}` }}
                  >
                    <div className="text-2xl sm:text-3xl font-light tabular-nums" style={{ color: textColor }}>
                      {String(val).padStart(2, '0')}
                    </div>
                    <div className="text-[10px] tracking-wider uppercase mt-1 font-sans" style={{ color: textMuted }}>
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ─── Section 4: Event Details ──────────────────────────── */}
      {timeline && timeline.length > 0 && (
        <section className="px-5 py-20" style={{ background: cardBg }} data-testid="event-details-section">
          <div className="max-w-sm mx-auto">
            <Reveal>
              <p className="text-xs tracking-[0.3em] uppercase mb-8 font-sans text-center" style={{ color: accent }}>
                Event Details
              </p>
            </Reveal>
            <div className="space-y-4">
              {timeline.map((event, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <div
                    className="rounded-2xl p-5"
                    style={{ background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff', border: `1px solid ${borderC}` }}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: `${accent}15` }}
                      >
                        <Clock className="w-4 h-4" style={{ color: accent }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm mb-1" style={{ color: textColor }}>
                          {event.title || 'Event'}
                        </h3>
                        {event.time && (
                          <p className="text-xs font-sans mb-2 flex items-center gap-1.5" style={{ color: textMuted }}>
                            <Calendar className="w-3 h-3" />
                            {event.time}
                          </p>
                        )}
                        {event.description && (
                          <p className="text-xs leading-relaxed" style={{ color: textMuted }}>
                            {event.description}
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Map CTA */}
                    {(venue_name || venue_address) && i === 0 && (
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(venue_address || venue_name)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 flex items-center gap-1.5 text-xs font-sans font-medium"
                        style={{ color: accent }}
                      >
                        <MapPin className="w-3 h-3" />
                        Open Map
                      </a>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Section 5: Our Story ──────────────────────────────── */}
      {story_text && !invitation_message && (
        <section className="px-5 py-20" data-testid="story-section">
          <div className="max-w-sm mx-auto text-center">
            <Reveal>
              <p className="text-xs tracking-[0.3em] uppercase mb-6 font-sans" style={{ color: accent }}>
                Our Story
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-sm leading-relaxed" style={{ color: textColor, opacity: 0.8 }}>
                {story_text}
              </p>
            </Reveal>
          </div>
        </section>
      )}

      {/* ─── Section 6: Gallery ────────────────────────────────── */}
      {gallery_images && gallery_images.length > 0 && (
        <section className="px-5 py-20" data-testid="gallery-section">
          <div className="max-w-sm mx-auto">
            <Reveal>
              <p className="text-xs tracking-[0.3em] uppercase mb-8 font-sans text-center" style={{ color: accent }}>
                Gallery
              </p>
            </Reveal>
            <div className="grid grid-cols-2 gap-2">
              {gallery_images.map((img, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div
                    className={`overflow-hidden rounded-xl cursor-pointer transition-transform hover:scale-[1.02] ${i === 0 ? 'col-span-2 aspect-[16/10]' : 'aspect-square'}`}
                    onClick={() => setLightboxImg(img)}
                  >
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Section 7: RSVP ───────────────────────────────────── */}
      <section
        ref={rsvpRef}
        className="px-5 py-20"
        style={{ background: cardBg }}
        data-testid="rsvp-section"
      >
        <div className="max-w-sm mx-auto">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase mb-3 font-sans text-center" style={{ color: accent }}>
              RSVP
            </p>
            <p className="text-center text-sm mb-8" style={{ color: textMuted }}>
              We'd love to know if you can make it
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <RSVPForm
              onSubmit={onSubmitRSVP}
              colorTheme={color_theme}
              fontStyle={font_style}
              isSubmitting={isSubmitting}
              submitSuccess={submitSuccess}
            />
          </Reveal>
        </div>
      </section>

      {/* ─── Section 8: Map / Directions ───────────────────────── */}
      {(venue_name || venue_address) && (
        <section className="px-5 py-20" data-testid="map-section">
          <div className="max-w-sm mx-auto text-center">
            <Reveal>
              <p className="text-xs tracking-[0.3em] uppercase mb-8 font-sans" style={{ color: accent }}>
                How to Get There
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div
                className="rounded-2xl p-6 mb-4"
                style={{ background: cardBg, border: `1px solid ${borderC}` }}
              >
                <MapPin className="w-6 h-6 mx-auto mb-3" style={{ color: accent }} />
                <h3 className="font-medium text-sm mb-1" style={{ color: textColor }}>{venue_name}</h3>
                {venue_address && (
                  <p className="text-xs" style={{ color: textMuted }}>{venue_address}</p>
                )}
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="flex gap-3 justify-center">
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(venue_address || venue_name)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-2.5 rounded-full text-xs font-sans font-medium tracking-wider transition-all hover:scale-105"
                  style={{ background: accent, color: '#fff' }}
                >
                  Google Maps
                </a>
                <a
                  href={`https://maps.apple.com/?q=${encodeURIComponent(venue_address || venue_name)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-2.5 rounded-full text-xs font-sans font-medium tracking-wider transition-all hover:scale-105"
                  style={{ background: 'transparent', color: textColor, border: `1px solid ${borderC}` }}
                >
                  Apple Maps
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ─── Section 9: FAQ ────────────────────────────────────── */}
      <section className="px-5 py-20" style={{ background: cardBg }} data-testid="faq-section">
        <div className="max-w-sm mx-auto">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase mb-8 font-sans text-center" style={{ color: accent }}>
              Questions & Answers
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              {faqs.map((item, i) => (
                <FAQItem key={i} q={item.q} a={item.a} accent={accent} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── Section 10: Closing CTA ───────────────────────────── */}
      <section className="px-5 py-24 text-center" data-testid="closing-section">
        <div className="max-w-sm mx-auto">
          <Reveal>
            <div className="w-12 h-px mx-auto mb-8" style={{ background: accent, opacity: 0.4 }} />
            <p className="text-sm leading-relaxed mb-2" style={{ color: textMuted }}>
              We can't wait to celebrate with you
            </p>
            <h2 className="text-2xl sm:text-3xl font-light mb-8" style={{ color: textColor }}>
              {couple_names || 'See you there'}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <button
              onClick={scrollToRSVP}
              className="px-10 py-3.5 rounded-full text-xs font-sans font-medium tracking-widest uppercase transition-all hover:scale-105"
              style={{ background: accent, color: '#fff', boxShadow: `0 4px 20px ${accent}44` }}
              data-testid="closing-rsvp-cta"
            >
              RSVP Now
            </button>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-12 text-xs font-sans" style={{ color: textMuted, opacity: 0.5 }}>
              Made with love
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── Lightbox ──────────────────────────────────────────── */}
      {lightboxImg && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setLightboxImg(null)}
        >
          <motion.img
            src={lightboxImg}
            alt=""
            className="max-w-full max-h-[85vh] rounded-lg object-contain"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
          />
          <button
            className="absolute top-6 right-6 text-white/60 hover:text-white text-2xl"
            onClick={() => setLightboxImg(null)}
          >
            &times;
          </button>
        </motion.div>
      )}

      {/* Bottom padding for sticky button */}
      <div className="h-20" />
    </div>
  );
}

// ─── Helper: detect dark backgrounds ───────────────────────────────
function isColorDark(hex) {
  if (!hex) return false;
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}
